import { useRpQuery } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import type {
    SignDocumentInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useCreateCheckoutAnalytics } from '~/composables/analytics/create-checkout'

export function useCreateCheckout() {
    const query = useRpQuery()
    const { emitCreateCheckoutEvent } = useCreateCheckoutAnalytics()
    return useMutation({
        mutationFn: (facilityId: string) =>
            query(
                graphql(/** GraphQL */ `
                    mutation CreateCheckout($facilityId: ID!) {
                        createCheckout(facilityId: $facilityId) {
                            __typename
                            ... on CreateCheckoutResult {
                                recordId
                                ...AnalyticsCreateCheckoutResultFragment
                            }
                            ... on CreateCheckoutIncomplete {
                                count
                                results
                            }
                        }
                    }
                `),
                {
                    facilityId
                }
            ),
        onSuccess(data) {
            if (
                data.createCheckout !== null &&
                data.createCheckout.__typename === 'CreateCheckoutResult'
            ) {
                emitCreateCheckoutEvent(data.createCheckout)
            }
        }
    })
}

// Update the contract and create a checkout
export function useUpdateContractCheckoutNowMutation() {
    const query = useRpQuery()
    const activeFacility = useActiveFacility()
    const { emitCreateCheckoutEvent } = useCreateCheckoutAnalytics()

    return useMutation({
        mutationFn: (mutationData: UpdateContractInput) =>
            query(
                graphql(/** @lang GraphQL */ `
                    mutation UpdateContractCheckoutNow(
                        $input: UpdateContractInput!
                        $facilityId: ID!
                    ) {
                        updateContract(input: $input) {
                            __typename
                            ... on UpdateContractResult {
                                recordId
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    message
                                }
                            }
                        }
                        createCheckout(facilityId: $facilityId) {
                            __typename
                            ... on CreateCheckoutResult {
                                recordId
                                ...AnalyticsCreateCheckoutResultFragment
                            }
                            ... on CreateCheckoutIncomplete {
                                count
                            }
                        }
                    }
                `),
                {
                    input: mutationData,
                    facilityId: activeFacility.value.id
                }
            ),
        onSuccess(data) {
            if (
                data.createCheckout !== null &&
                data.createCheckout.__typename === 'CreateCheckoutResult'
            ) {
                emitCreateCheckoutEvent(data.createCheckout)
            }
        }
    })
}

// Sign the document on a contract and create a checkout
export function useSignDocumentCheckoutNowMutation() {
    const query = useRpQuery()
    const { emitCreateCheckoutEvent } = useCreateCheckoutAnalytics()
    return useMutation({
        mutationFn: (mutationData: {
            input: SignDocumentInput
            facilityId: string
        }) =>
            query(
                graphql(/** @lang GraphQL */ `
                    mutation SignDocumentCheckoutNow(
                        $input: SignDocumentInput!
                        $facilityId: ID!
                    ) {
                        signDocument(input: $input) {
                            __typename
                            ... on SignDocumentResult {
                                recordId
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    path
                                    message
                                }
                            }
                        }
                        createCheckout(facilityId: $facilityId) {
                            __typename
                            ... on CreateCheckoutResult {
                                recordId
                                ...AnalyticsCreateCheckoutResultFragment
                            }
                            ... on CreateCheckoutIncomplete {
                                count
                            }
                        }
                    }
                `),
                {
                    input: mutationData.input,
                    facilityId: mutationData.facilityId
                }
            ),
        onSuccess(data) {
            if (
                data.createCheckout !== null &&
                data.createCheckout.__typename === 'CreateCheckoutResult'
            ) {
                emitCreateCheckoutEvent(data.createCheckout)
            }
        }
    })
}
